import axios, { AxiosResponse } from 'axios';

import { axiosConfigAuth } from '../middleware';
import { API_UTM_SCHEMES } from '../urls';

/**
 * Схема разбора упакованных UTM-меток, привязанная к рекламному кабинету (этап 3).
 *
 * Все ответы в конверте { success, code, message, data }, как у custom-groups.
 * Школа: в GET и DELETE уходит query-параметром, в POST и PATCH — в теле.
 * Доступ: владелец школы и суперадмин; сотруднику бэкенд отвечает 403 на все маршруты.
 * ТЗ бэка: docs/UTM_STAGE3_TZ.md, отличия — UTM_STAGE3_BACKEND_CHANGES.md
 */
export const utmSchemesApi = {
    /**
     * Схема кабинета (у кабинета она одна) и глобальная библиотека со статистикой по школе,
     * плюс справочники параметров и меток и признак `school_task_pending`.
     */
    getList: (
        schoolId: number | string,
        integrationId: number
    ): Promise<AxiosResponse> => {
        return axios.get(
            `${API_UTM_SCHEMES}/list?school_id=${schoolId}&integration_id=${integrationId}`,
            axiosConfigAuth()
        );
    },

    /**
     * Разобрать вставленную ссылку в черновик схемы: тип разбора, разделители, сигнатура,
     * поля с догадками параметров. Принимается и шаблон с подстановками, и реальная ссылка.
     */
    analyzeUrl: (
        schoolId: number | string,
        integrationId: number,
        url: string
    ): Promise<AxiosResponse> => {
        return axios.post(
            `${API_UTM_SCHEMES}/analyze-url`,
            { school_id: schoolId, integration_id: integrationId, url },
            axiosConfigAuth()
        );
    },

    /**
     * Предпросмотр черновика на реальных значениях школы — до сохранения схемы,
     * поэтому id не нужен, но нужен весь заголовок: без типа и разделителей парсер не работает.
     */
    preview: (
        schoolId: number | string,
        scheme: Record<string, unknown>,
        limit = 10
    ): Promise<AxiosResponse> => {
        return axios.post(
            `${API_UTM_SCHEMES}/preview`,
            { school_id: schoolId, scheme, limit },
            axiosConfigAuth()
        );
    },

    /** Создать схему кабинета. Вторая схема запрещена: бэкенд ответит 400 scheme_exists */
    create: (payload: Record<string, unknown>): Promise<AxiosResponse> => {
        return axios.post(`${API_UTM_SCHEMES}/create`, payload, axiosConfigAuth());
    },

    /** Изменить схему кабинета */
    update: (schemeId: number, payload: Record<string, unknown>): Promise<AxiosResponse> => {
        return axios.put(`${API_UTM_SCHEMES}/${schemeId}`, payload, axiosConfigAuth());
    },

    /**
     * Диагностика меток школы: что упаковано, что разобрано, где не сработали подстановки,
     * какие форматы остались без схемы и в каких полях всегда одно значение.
     * Строится 3–5 секунд.
     *
     * Период (`ГГГГ-ММ-ДД`, обе границы включительно) отбирает значения по дате регистрации:
     * в отчёт попадают только те, с которыми есть хотя бы одна регистрация за период.
     * Передавать нужно обе границы сразу или ни одной — иначе бэкенд ответит 400.
     */
    getReport: (
        schoolId: number | string,
        dateFrom?: string,
        dateTo?: string
    ): Promise<AxiosResponse> => {
        const period = dateFrom && dateTo
            ? `&date_from=${dateFrom}&date_to=${dateTo}`
            : '';

        return axios.get(
            `${API_UTM_SCHEMES}/report?school_id=${schoolId}${period}`,
            axiosConfigAuth()
        );
    },

    /** Переразобрать все упакованные метки школы. При незавершённой задаче — 409 */
    reparseSchool: (schoolId: number | string): Promise<AxiosResponse> => {
        return axios.post(
            `${API_UTM_SCHEMES}/reparse-school`,
            { school_id: schoolId },
            axiosConfigAuth()
        );
    },

    /** Статус задачи переразбора: status 0 в очереди, 1 выполняется, 2 готово, 3 ошибка */
    getTask: (
        schoolId: number | string,
        taskId: number
    ): Promise<AxiosResponse> => {
        return axios.get(
            `${API_UTM_SCHEMES}/tasks/${taskId}?school_id=${schoolId}`,
            axiosConfigAuth()
        );
    },

    /**
     * Включить или выключить схему. Выключенная не применяется к новым значениям,
     * прежний результат остаётся; с `reparse` ставится задача переразбора.
     */
    setActive: (
        schoolId: number | string,
        schemeId: number,
        isActive: boolean,
        reparse = false
    ): Promise<AxiosResponse> => {
        return axios.patch(
            `${API_UTM_SCHEMES}/${schemeId}/active`,
            { school_id: schoolId, is_active: isActive, reparse },
            axiosConfigAuth()
        );
    },

    /**
     * Удалить схему. Если ею что-то разобрано, бэкенд ставит задачу на пересчёт
     * (в ответе `task_id`), и значения переразбираются другими схемами.
     */
    deleteScheme: (
        schoolId: number | string,
        schemeId: number
    ): Promise<AxiosResponse> => {
        return axios.delete(
            `${API_UTM_SCHEMES}/${schemeId}?school_id=${schoolId}`,
            axiosConfigAuth()
        );
    },

    /**
     * Переразобрать историю по схеме. При незавершённой задаче школы бэкенд отвечает 409 —
     * вторую задачу ставить нельзя.
     */
    reparse: (
        schoolId: number | string,
        schemeId: number
    ): Promise<AxiosResponse> => {
        return axios.post(
            `${API_UTM_SCHEMES}/${schemeId}/reparse`,
            { school_id: schoolId },
            axiosConfigAuth()
        );
    },
};
